import { useQuery } from '@apollo/client/react';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import BookingChecklistItemState from '../../booking/components/BookingChecklistItemState';
import useAlterBookingState from '../../booking/hooks/useAlterBookingState';
import { InvoiceViewDialog } from '../../booking/components/operation/InvoiceView';
import ReviewDialog from '../../booking/components/ReviewDialog';
import assert from '../../core/util/assert';
import { Booking } from '../../domain/booking';
import SpinnerIcon from '../../main/component/icon/SpinnerIcon';
import { checklistQuery } from '../../main/queries';

interface Props {
    bookingId: number;
}

const CheckoutChecklist = ({ bookingId }: Props) => {
    const { data, error, loading } = useQuery<{
        booking: Booking;
    }>(checklistQuery, { variables: { id: bookingId } });
    const [alterBookingState] = useAlterBookingState(bookingId);
    const [createInvoiceModalOpen, setCreateInvoiceModalOpen] = useState(false);
    const [reviewDialogOpen, setReviewDialogOpen] = useState(false);

    if (loading) return <SpinnerIcon />;

    if (error) return <p>Error :(</p>;

    assert(data);

    const { booking } = data;

    return (
        <Box>
            <Typography variant="h5" component="h2">
                Checkout checklist
            </Typography>
            <FormGroup>
                <BookingChecklistItemState
                    label={'Accommodation verified'}
                    booking={booking}
                    statusField="accommodationVerified"
                />
                <BookingChecklistItemState
                    label={'Keys secured'}
                    booking={booking}
                    statusField="keysSecured"
                />
                <Box>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={!!booking.hasReview}
                                color="success"
                                onChange={() => {
                                    if (!booking.hasReview) {
                                        setReviewDialogOpen(true);
                                    } else {
                                        alterBookingState({
                                            variables: {
                                                id: booking.id,
                                                fields: { hasReview: false },
                                            },
                                            optimisticResponse: {
                                                alterBookingState: {
                                                    __typename: 'Booking',
                                                    id: booking.id,
                                                    reference:
                                                        booking.reference,
                                                    importedToGovernmentPortal:
                                                        booking.importedToGovernmentPortal,
                                                    detailsRequested:
                                                        booking.detailsRequested,
                                                    detailsRecorded:
                                                        booking.detailsRecorded,
                                                    hasInstructions:
                                                        booking.hasInstructions,
                                                    accommodationReady:
                                                        booking.accommodationReady,
                                                    accommodationVerified:
                                                        booking.accommodationVerified,
                                                    keysSecured:
                                                        booking.keysSecured,
                                                    hasReview: false,
                                                },
                                            },
                                        });
                                    }
                                }}
                            />
                        }
                        label={'Review'}
                    />
                    {reviewDialogOpen && (
                        <ReviewDialog
                            booking={booking}
                            onClose={() => {
                                setReviewDialogOpen(false);
                            }}
                        />
                    )}
                </Box>
                {!booking.invoiceId && (
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={!!booking.invoiceId}
                                color="success"
                                onClick={() => {
                                    if (!booking.invoiceId) {
                                        setCreateInvoiceModalOpen(true);
                                    }
                                }}
                            />
                        }
                        label={'Invoice'}
                    />
                )}
                {!!booking.invoiceId && (
                    <BookingChecklistItemState
                        label={'Invoice'}
                        booking={booking}
                        statusField="invoiceId"
                    />
                )}
                {createInvoiceModalOpen && (
                    <InvoiceViewDialog
                        bookingId={bookingId}
                        onFocusLost={() => {
                            setCreateInvoiceModalOpen(false);
                        }}
                    />
                )}
            </FormGroup>
        </Box>
    );
};

export default CheckoutChecklist;
